/*
* Google Enhanced Ecommerce Object
*
* Basket related logic and functionality for Google Enhanced Ecommerce.
* https://confluence.int.plus.net/display/DIGITAL/Google+Enhanced+Ecommerce
*/
const basket = {

    isInitialised: false,

    /*
     * Initialise basket state and setup basket event listeners
     *
     * @returns {void}
     */
    initialise: () => {

        if (!gaEcommerce.core.checkStorageCapabilities()) return;

        const basket = gaEcommerce.basket;
        basket.real = {};
        basket.hasDiscountChanged = false;

        basket.broadbandStepSelector = '[data-step="package"]';
        basket.phoneStepSelector = '[data-step="callplan"]';
        basket.extrasStepSelector = '[data-step="phone"]';
        basket.signupForm = document.querySelector('#directOfferings');
        basket.broadbandStep = basket.signupForm.querySelector(basket.broadbandStepSelector);
        basket.phoneStep = basket.signupForm.querySelector(basket.phoneStepSelector);
        basket.extrasStep = basket.signupForm.querySelector(basket.extrasStepSelector);
        basket.buttonSelector = '.product-button';
        basket.inputSelector = '.productOptions-option-description-input';
        basket.broadbandButtonsSelector = basket.broadbandStepSelector + ' ' + basket.buttonSelector;
        basket.lineOnlyButtonSelector = backet.phoneStepSelector + ' ' + basket.buttonSelector + '[data-callplan="COS_CallChargesTariff_LineOnly"]';
        basket.radioButtonsSelector = basket.inputSelector + '[type="radio"]';
        basket.checkboxesSelector = basket.inputSelector + '[type="checkbox"]';

        // Stores the ecommerce basket
        let products = sessionStorage.getItem('ecomProducts');

        if (products !== null) {

            try {
                products = JSON.parse(products);

                const propertiesList = [
                    'list',
                    'position',
                    'timeUntilPromoExpires',
                    'quantity',
                    'interaction',
                    'dimension48'
                ];

                basket.sanitisedProducts = products;

                basket.products = products.map(JSON.parse).map(function (product) {
                    propertiesList.map(function (property) {
                        if (typeof product[property] !== 'undefined') {
                            delete product[property];
                        }
                    });

                    return product;

                }).map(JSON.stringify);
            } catch (e) {
                basket.products = [];
                basket.sanitisedProducts = [];
            }
        } else {
            basket.products = [];
            basket.sanitisedProducts = [];
        }

        // Attach event handlers to inputs and buttons
        gaEcommerce.events.registerBasketEventHandlers(basket);

        // Get the client time on page load
        gaEcommerce.clientTimeOnLoad = Math.floor(Date.now() / 1000);

        // Fire basket add events for products already in basket when customer starts signup
        if (basket.signupForm.dataset.selectedProductAvailable) {
            gaEcommerce.basket.handlePreSelections();
        }

        gaEcommerce.util.clearSelection();

        if (gaEcommerce.rules.findProducts([{ category: "Incentives/Phone/Bundles" }]).length > 0) {
            gaEcommerce.rules.addBundleCouponToAll();
        } else {
            gaEcommerce.rules.removeBundleCouponFromAll();
        }

        basket.replacePlaceholders();

        basket.isInitialised = true;
    },

    /*
     * This is called if the customer has been referred from a deals page and automatically get's the product
     * added to their basket
     *
     * @param {string} promo
     * @returns {void}
     */
    setSelectedPromoCode: (promo) => {
        if (promo) {
            const productElement = document.querySelector('[data-promo="' + promo + '"]')?.closest('[data-ecom-product]');

            if (productElement) {
                gaEcommerce.basket.add(productElement, 'non-interaction');
            }
        }
    },

    /*
     * Check if there is a pre-signup selection in storage, and handle it if there is
     *
     * @returns {void}
     */
    handlePreSelections: () => {
        const selection = gaEcommerce.util.getSelection();

        if (typeof selection !== 'string' || selection.length < 1) {
            return;
        }

        const product = gaEcommerce.util.parseJsonObj(selection);

        // Set contract length on line rental to match broadband
        gaEcommerce.basket.updateLineRentalVariant(product.variant);

        gaEcommerce.promos.addBundle(product, 'interaction');
    },

    /*
     * Handle basket button clicks
     *
     * @param {object} button DOM element
     * @param {function} callback Function to call after button click is handled
     * @param {boolean} isBundle whether it's a bundle or not
     *
     * @returns {void}
     */
    handleButton: (button, callback, isBundle) => {
        const ecomProductElem = button.closest('[data-ecom-product]');

        if (ecomProductElem) {
            const product = gaEcommerce.util.parseJsonObj(ecomProductElem.dataset.ecomProduct);

            if (isBundle || product.id?.indexOf('|') >= 0) {
                gaEcommerce.promos.addBundle(product, 'interaction');
            } else {
                gaEcommerce.basket.processAdd(ecomProductElem, 'interaction');
            }

            if (typeof callback === 'function') {
                callback();
            }
        }
    },

    /*
     * Handle changes to radio buttons
     * @todo could/should this be covered by data-ecom-rules?
     *
     * @param {object} selectedRadioButton DOM element
     * @param {NodeList} radioButtons list of button elements
     *
     * @returns {void}
     */
    handleRadioButtons: (selectedRadioButton, radioButtonGroup) => {

        // What did the user ask us to do? Add or remove a product?
        // Note: not a child of data-ecom-product = remove (e.g. 'No Voicemail') button
        const userAction = selectedRadioButton.closest('[data-ecom-product]')
                ? 'add'
                : 'remove';

        radioButtons.forEach((item) => {
            const ecomProductElem = item.closest('[data-ecom-product]');

            if (ecomProductElem) {
                const action = item.checked ? 'add' : 'remove';

                // Tell the basket to TRY to add/remove item depending on whether it's selected
                // Add: only gets added if item NOT in basket
                // Remove: only gets removed if item IS in basket
                if (action === 'add') {
                    gaEcommerce.basket.processAdd(ecomProductElem, 'interaction');
                } else if (action  === 'remove') {
                    // Did the user ask to remove an item or is the script removing it to change the variant?
                    var interaction = (userAction === 'remove') ? 'interaction' : 'non-interaction';

                    gaEcommerce.basket.processRemove($ecomProductElem, interaction);
                }
            }
        });
    },

    /*
     * Handle checkbox events
     * @todo could/should this be covered by data-ecom-rules?
     *
     * @param {object} checkbox DOM element
     * @returns {void}
     */
    handleCheckbox: (checkbox) => {
        const ecomProductElem = checkbox.closest('[data-ecom-product]');

        if (ecomProductElem) {
            gaEcommerce.basket[
                checkbox.checked ? 'processAdd' : 'processRemove'
            ](ecomProductElem, 'interaction');
        }
    },

    /*
     * Scrape the extras page
     *
     * @param {string} interaction Whether or not this was a user interaction
     * @returns {void}
     */
    scrapeExtrasPage: (interaction) => {
        const productsToAdd = [];

        document.querySelectorAll('[data-step="phone"] [data-ecom-product]')
            .forEach((item) => {
                if (item.querySelectorAll(gaEcommerce.basket.inputSelector + ':checked').length) {
                    productsToAdd.push(thisProduct);
                }
            });

        if (productsToAdd.length) {
            gaEcommerce.basket.add(productsToAdd, interaction);
        }
    },

    /*
     * Compares our GA basket with the "real" basket to see if the discounts match
     *
     * IMPORTANT this code needs to wait for all basket updates that happen in a short period of time. This "wait"
     * is implemented via a 200ms timeout that resets each time the function is called
     *
     * @returns {void}
     */
    processBasket: () => {

        // Stop the timeout to "throttle" this call
        if (gaEcommerce.basket.processBasketTimeout) {
            clearTimeout(gaEcommerce.basket.processBasketTimeout);
        }

        // Restart the timeout
        gaEcommerce.basket.processBasketTimeout = setTimeout(() => {
            const hasDiscountChanged = gaEcommerce.basket.hasDiscountChanged;
            gaEcommerce.basket.hasDiscountChanged = false;

            if (
                hasDiscountChanged
                && typeof gaEcommerce.basket.real.data.activeUpfrontCharges.items !== 'undefined'
            ) {

                // Update broadband discounts if necessary
                const gaBroadbandProduct = gaEcommerce.basket.findSelectedBroadband();
                if (gaBroadbandProduct !== null) {
                    gaEcommerce.promos.updateBroadbandDiscount(gaBroadbandProduct)
                }
            }

            clearTimeout(gaEcommerce.basket.processBasketTimeout);

        }, 200);
    },

    /*
     * Find the currently selected broadband produxr
     *
     * @returns {null|object}
     */
    findSelectedBroadband: () => {
        const gaBroadbandProducts = gaEcommerce.rules.findProducts([{
            category: {
                type: "regex",
                pattern: "^Services\/Broadband\/"
            }
        }]);

        return (gaBroadbandProducts.length === 1)
            ? gaBroadbandProducts[0]
            : null;
    },

    /*
     * Find the broadband product in the "real" basket
     *
     * @param {object} gaBroadbandProduct
     * @returns {object}
     */
    findBasketBroadband: (gaBroadbandProduct) => {
        let basketBroadbandProduct = null;

        gaEcommerce.basket.real.data.activeUpfrontCharges.items.map((item) => {
            if (item.name.indexOf(gaBroadbandProduct.name) === 0) {
                basketBroadbandProduct = item;
            }
        });

        return basketBroadbandProduct;
    },

    /*
     * Add a product based from a data object (rather than an element)
     *
     * @param {object} productData
     * @param {string} interaction
     * @param {boolean} processRules
     *
     * @returns {void}
     */
    addProductData: (productData, interaction, processRules) => {

        processRules = typeof processRules === 'undefined' ? true : processRules;
        interaction = typeof interaction === 'undefined' ? 'interaction': interaction;

        const itemsToAdd = [],
              productDataArray = (productData instanceof Array)
                ? productData
                : [productData];

        productDataArray.map((data) => {
            const element = gaEcommerce.core.getProductElementById(data.id);

            if (element) {
                itemsToAdd.push({
                    element: element,
                    dataOverride: data
                });
            }
        });

        gaEcommerce.basket.processItems(itemsToAdd, [], interaction, processRules);
    },

    /*
     * Wraps the add function (which bubbles down through all the rules)
     * then processes the final basket object
     *
     * @param {object|array} productElements A single product element or list of product elements
     * @param {string} interaction Whether or not this was a user interaction
     * @param {boolean} processRules whether or not to process rules
     *
     * @returns {null|boolean}
     */
    processAdd: (productElements, interaction, processRules) => {
        processRules = typeof processRules === 'undefined' ? true : processRules;
        gaEcommerce.basket.add(productElements, interaction, processRules);
        gaEcommerce.basket.processBasket();
    },

    /*
     * Add product elements to the basket
     *
     * @param {object|array|NodeList} productElements A single product element or list of product elements
     * @param {string} interaction Whether or not this was a user interaction
     * @param {boolean} processRules whether or not to process rules
     *
     * @returns {null|boolean}
     */
    add: (productElements, interaction, processRules) => {
        // If only one product element given wrap it in an array so the loop still handles it
        const productElementsIterable =
            (productElements instanceof Array || productElements instanceof NodeList)
                ? productElements
                : [productElements];

        processRules = typeof processRules === 'undefined' ? true : processRules;

        const newProducts = [], // To be sent to GA
              interactionBoolean = (interaction !== 'non-interaction');

        // Loop through all products and sanitise each one
        productElementsIterable.forEach((productElementItem) => {
            let shouldProcessRules = processRules,
                dataOverride = {};

            if (productElementItem.hasOwnProperty('dataOverride')
                && productElementItem.hasOwnProperty('element')
            ) {
                if (productElementItem.hasOwnProperty('processRules')) {
                    shouldProcessRules = productElementItem.processRules;
                }

                dataOverride = productElementItem.dataOverride;
                productElementItem = productElementItem.element;
            }

            const rawProduct = gaEcommerce.util.mergeObject(
                {},
                gaEcommerce.util.parseJsonObj(productElementItem.dataset.ecomProduct),
                dataOverride
            );

            const rawProductString = JSON.stringify(rawProduct);

            if (gaEcommerce.basket.products.indexOf(rawProductString) === -1) {
                let sanitisedProduct = gaEcommerce.core.validateProduct(rawProduct);

                if (sanitisedProduct !== false) {

                    if (shouldProcessRules) {
                        gaEcommerce.rules.processRules(productElementItem, 'add');
                    }
                    // Add RAW product to the ecommerce basket array
                    // Stored as string so we can check what products are in the array easily
                    gaEcommerce.basket.products.push(rawProductString);

                    // Add SANITISED product to array of products to send to GA
                    sanitisedProduct = gaEcommerce.core.includeDynamicProductProperties(
                        productElementItem, sanitisedProduct
                    );
                    sanitisedProduct.interaction = interactionBoolean;

                    gaEcommerce.basket.sanitisedProducts.push(JSON.stringify(sanitisedProduct));
                    newProducts.push(sanitisedProduct);
                }
            }
        });

        // Send the fully formed array of products to GA
        if (newProducts.length > 0) {
            gaEcommerce.basket.saveBasket();
        }

        gaEcommerce.core.sendAddData(newProducts);
    },

    /*
     * Wraps the remove function (which bubbles down through all the rules)
     * then processes the final basket object
     *
     * @param {object|array} productElements A single product element or list of product elements
     * @param {string} interaction Whether or not this was a user interaction
     * @param {boolean} processRules whether or not to process rules
     *
     * @returns {null|boolean}
     */
    processRemove: (productElements, interaction, processRules) => {

        processRules = typeof processRules === 'undefined' ? true : processRules;
        gaEcommerce.basket.remove(productElements, interaction, processRules);
        gaEcommerce.basket.processBasket();
    },

    /*
     * Remove product elements from the basket
     *
     * @param {object|array} productElements A single product element or list of product elements
     * @param {string} interaction Whether or not the interaction was a user interaction
     * @param {boolean} processRules whether or not to process rules
     *
     * @returns {null|boolean}
     */
    remove: (productElements, interaction, processRules) => {
        // If only one product element given wrap it in an array so the loop still handles it
        const productElementsIterable =
            (productElements instanceof Array || productElements instanceof NodeList)
                ? productElements
                : [productElements];

        processRules = typeof processRules === "undefined" ? true : processRules;     

        // Array of products to send to GA
        const removedProducts = [], // To be sent to GA
              interactionBoolean = (interaction !== 'non-interaction');

        // Loop through all products and sanitise each one
        productElementsIterable.forEach((productElementItem) => {

            let shouldProcessRules = processRules,
                dataOverride = {};

            if (productElementItem.hasOwnProperty("dataOverride")
                && productElementItem.hasOwnProperty("element")
            ) {
                if (productElementItem.hasOwnProperty('processRules')) {
                    shouldProcessRules = productElementItem.processRules;
                }
                dataOverride = productElementItem.dataOverride;
                productElementItem = productElementItem.element;
            }

            const rawProduct = gaEcommerce.util.mergeObject(
                {},
                gaEcommerce.util.parseJsonObj(productElementItem.dataset.ecomProduct),
                dataOverride
            );

            const rawProductString = JSON.stringify(rawProduct),
                  productIndex = gaEcommerce.basket.products.indexOf(rawProductString);

            // Check it's actually in the basket
            if (productIndex !== -1) {

                let sanitisedProduct = gaEcommerce.core.validateProduct(rawProduct);

                if (sanitisedProduct !== false) {

                    // Remove RAW product from the ecommerce basket array
                    gaEcommerce.basket.products.splice(productIndex, 1);
                    gaEcommerce.basket.sanitisedProducts.splice(productIndex, 1);

                    if (shouldProcessRules) {
                        gaEcommerce.rules.processRules(productElementItem, 'remove');
                    }

                    // Add SANITISED product to array of products to send to GA
                    sanitisedProduct = gaEcommerce.core.includeDynamicProductProperties(
                        productElementItem,
                        sanitisedProduct
                    );
                    sanitisedProduct.interaction = interactionBoolean;
                    removedProducts.push(sanitisedProduct);
                }
            }
        });

        if (removedProducts.length > 0) {
            gaEcommerce.basket.saveBasket();
        }

        gaEcommerce.core.sendRemoveData(removedProducts);
    },

    /*
     * Returns the products in the GA Basket
     *
     * @returns {Array}
     */
    getBasketContents: () =>
        gaEcommerce.basket.products
    ,

    /*
     * Process lists of products to add and remove
     *
     * @param {array} itemsToAdd
     * @param {array} itemsToRemove
     * @param {string} interaction
     * @param {boolean} processRules
     *
     * @returns {void}
     */
    processItems: (itemsToAdd, itemsToRemove, interaction, processRules) => {

        if (itemsToRemove.length) {
            gaEcommerce.basket.remove(itemsToRemove, interaction, processRules);
        }

        if (itemsToAdd.length) {
            // We call "processAdd" rather than just "add", because this action could mean another promo becomes
            // relevant
            gaEcommerce.basket.processAdd(itemsToAdd, interaction, processRules);
        }
    },

    /*
     * Add product elements to sessionStorage
     *
     * @returns {void}
     */
    saveBasket: () => {
        clientData.storage.setData({name: "ecomProducts", value: JSON.stringify(gaEcommerce.basket.sanitisedProducts)}, "sessionStorage");
    },

    /*
     * Called when the "I don't want home phone" button is clicked, removes phone products with interaction: true,
     * and all other products with interaction: false
     *
     * @returns {void}
     */
    removePhone: () => {
        // Remove all phone products with interaction true
        const phoneProducts = gaEcommerce.rules.findProducts([{
            category: {
                type: "regex",
                pattern: ".*\/Phone\/.*"
            }
        }]);

        gaEcommerce.basket.remove(
            phoneProducts.reduce(gaEcommerce.core.reduceElementsFromProduct, []),
            'interaction',
            false
        );

        gaEcommerce.basket.removeAllProducts('non-interaction', false);
    },

    /*
     * Remove all products from the GA basket
     *
     * @param {string} interaction
     * @param {boolean} processRules
     *
     * @returns {void}
     */
    removeAllProducts: (interaction, processRules) => {
        const productElements = gaEcommerce.basket.getBasketContents()
            .map(gaEcommerce.util.parseJsonArray)
            .reduce(gaEcommerce.core.reduceElementsFromProduct, []);

        gaEcommerce.basket.remove(productElements, interaction, processRules);
    },

    /*
     * Adds product ecom attributes to the element based on the placeholder data passed in
     *
     * @param {Object} element DOM Element
     * @param {object} data placeholder data
     *
     * @returns {void}
     */
    addProductDataAttributeToElement: (element, data) => {
        const matchingElements = document.querySelectorAll(
            '[data-broadband-id="' + data.code + '"][data-ecom-product][data-contract="' + data.contract + ' month contract"]'
        );

        const matches = Array.prototype.slice.apply(matchingElements).reduce((matches, elem) => {
            const ecomProduct = gaEcommerce.util.parseJsonObj(elem.dataset.ecomProduct);

            let match = true;

            // If we have a promo, and the coupon doesn't exist or doesn't match, then it's not a match
            if (data.promo && (typeof ecomProduct.coupon === 'undefined' || ecomProduct.coupon !== data.promo)) {
                match = false;
            }

            // If we don't have a promo, and the coupon exist and isn't empty, then it's not a match
            if (!data.promo && typeof ecomProduct.coupon !== 'undefined' && ecomProduct.coupon !== '') {
                match = false;
            }

            if (typeof ecomProduct.price === 'undefined' || ecomProduct.price !== data.price) {
                match = false;
            }

            if (match) {
                matches.push(elem);
            }

            return matches;
        }, []);

        if (matches.length) {
            element.dataset.ecomProduct = matches[0].dataset.ecomProduct;
            if (typeof matches[0].dataset.ecomRules !== 'undefined') {
                element.dataset.ecomRules = matches[0].dataset.ecomRules;
            }
        }
    },

    /*
     * Finds elements with data-ecom-product-placeholder attributes and applies the relevant data-ecom-product
     * attribute if one is found.
     */
    replacePlaceholders: () => {
        const productPlaceholders = document.querySelectorAll('[data-ecom-product-placeholder]');

        productPlaceholders.forEach((elem) => {

            if (elem.dataset.ecomProductPlaceholder !== "") {
                gaEcommerce.basket.addProductDataAttributeToElement(
                    elem, gaEcommerce.util.parseJsonObj(elem.dataset.ecomProductPlaceholder)
                );
                elem.dataset.ecomProductPlaceholder = "";
            }
        });
    },

    /*
     * Set contract length on line rental to match broadband
     * (updates the data-ecom-product attribute)
     *
     * @param {string} contractLength
     * @return {void}
     */
    updateLineRentalVariant: (contractLength) => {
        const lineRentalProduct = document
            .querySelector('#product_options_phone_C_LineRentalPrepayment_COS_YN_No')
            ?.closest('data-ecom-product');

        if (lineRentalProduct) {
            gaEcommerce.core.changeDataEcomProductAttribute(
                lineRentalProduct,
                {variant: contractLength}
            );
        } else {
            console.warn('GA ecommerce - could not find line rental product');
        }
        
    }
};

export default basket;
